import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Popup from "../../../components/Popup";
import {
  Badge,
  EmptyState,
  IconButton,
  Page,
  PageHeader,
  Pagination,
  SearchInput,
  TableSkeleton,
  usePagination,
} from "../../../components/ui";
import { getCategories } from "../../../store/actions/uploadMediaAction";
import {
  deleteCategory,
  getAllGenreCategories,
  getCategory,
} from "../../../services/genres";
import { capitalize, matches } from "../../../utils/list";

const TYPE_COLORS = {
  movies: "blue",
  "tv shows": "violet",
  podcasts: "green",
  channels: "amber",
};

/** "Movies" → "Video", "tv shows" → "TV shows" (legacy display rules). */
const typeLabel = (genreType) => {
  const type = genreType.toLowerCase();
  if (type === "movies") return "video";
  return type.includes("tv") ? type.replace("tv", "TV") : type;
};

/** The API returns one genre per content type: group them by name. */
const groupByName = (genres) =>
  (genres || []).reduce((groups, genre) => {
    const group = groups.find((g) => g.name === genre.name);
    const entry = { genre_type: genre.genre_type, _id: genre._id };
    if (group) group.genres.push(entry);
    else groups.push({ name: genre.name, genres: [entry] });
    return groups;
  }, []);

const CategoryList = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  useEffect(() => {
    dispatch(getCategories([]));
    getAllGenreCategories().then((result) => {
      setGenres(result);
      setLoading(false);
    });
  }, [dispatch]);

  const categories = useMemo(() => groupByName(genres), [genres]);
  const filtered = useMemo(
    () => categories.filter((category) => matches(category.name, search)),
    [categories, search]
  );
  const pagination = usePagination(filtered, 10, [search]);

  const handleEdit = (category) => {
    Promise.all(category.genres.map((genre) => getCategory(genre._id))).then(
      (responses) => {
        dispatch(getCategories(responses));
      }
    );
    history.push("/dashboard/update-category");
  };

  const handleDelete = async (category) => {
    try {
      await Promise.all(
        category.genres.map((genre) => deleteCategory(genre._id))
      );
      toast.success("Genre Deleted Successfully!", {
        progressStyle: { background: "var(--iq-primary)" },
      });
      setGenres(await getAllGenreCategories());
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Page>
      <PageHeader
        title={t("Categories")}
        subtitle={loading ? " " : `${categories.length} ${t("genres")}`}
        actions={
          <Link to="/dashboard/add-genre" className="ui-btn">
            <i className="ri-add-line" aria-hidden="true"></i>
            {t("Add Genre")}
          </Link>
        }
      />

      <section className="ui-card">
        <div className="ui-toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t("Search a genre")}
          />
        </div>

        {loading ? (
          <TableSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-price-tag-3-line">
            {t("No Data Found")}
          </EmptyState>
        ) : (
          <>
            <div className="ui-table-wrap">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th>{t("Category Name")}</th>
                    <th>{t("Content type")}</th>
                    <th className="ui-cell-actions">
                      <span className="sr-only">{t("action")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagination.pageItems.map((category) => (
                    <tr key={category.name}>
                      <td style={{ fontWeight: 600 }}>{category.name}</td>
                      <td>
                        <div
                          style={{ display: "flex", flexWrap: "wrap", gap: 6 }}
                        >
                          {category.genres.map((genre) => (
                            <Badge
                              key={genre._id}
                              color={
                                TYPE_COLORS[genre.genre_type.toLowerCase()] ||
                                "slate"
                              }
                            >
                              {capitalize(t(typeLabel(genre.genre_type)))}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="ui-cell-actions">
                        <IconButton
                          icon="ri-pencil-line"
                          label={t("Edit")}
                          onClick={() => handleEdit(category)}
                        />
                        <IconButton
                          icon="ri-delete-bin-line"
                          label={t("Delete")}
                          tone="danger"
                          onClick={() => setCategoryToDelete(category)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination {...pagination} onChange={pagination.setPage} />
          </>
        )}
      </section>

      {categoryToDelete && (
        <Popup
          open={true}
          setOpen={() => setCategoryToDelete(null)}
          deleteHandle={() => handleDelete(categoryToDelete)}
        >
          <div className="model-wrapper">
            {t("Are you sure, you want to delete")} {categoryToDelete.name} ?
          </div>
        </Popup>
      )}
    </Page>
  );
};

export default CategoryList;
